import { Dropdown, TextArea, Toggle } from "pepsico-ds";
import TextInputField from "../../../../../utils/Fields/TextInputField";
import { useForm } from "react-hook-form";
import { Typography, Box, useMediaQuery, Grid, Alert } from "@mui/material";

import "./ForumDetails.css";
import { useSelector } from "react-redux";

import { useCallback, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import formdetailsValidation from "./formDetailsValidations";
import DropZone from "../../../../../utils/Upload/Dropzone";
import ImageList from "../../LandingPageHero.js/ImageList";
import useSingleImageDrop from "../../../../../hooks/useSingleImageDrop";
import {
  autoPopulateFormFields,
  handleMultiSelectChange,
  handleSingleSelectChange,
  normalizeForumMedia,
} from "./ForumCommon";
import Loading from "../../../../../utils/Loader";
import useDebounce from "../../../../../hooks/useDebounce";
import useDuplicateTitleCheck from "../../../../../hooks/useDuplicateCheck";
import { BASE_URL } from "../../../../../constant/apiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { getAPI } from "../../../../../API/ApiService";
import { useDispatch } from "react-redux";
import { setAssociatedContent } from "../../../../../slices/forumBuilderSlice";
import fetchAndCacheImage from "../../../../../utils/fetchandCatchImages";

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  languageId: yup.object().nullable().required("Language is required"),
  description: yup.string(),
  sectorId: yup.array().min(1, "Sector is required"),
  countryId: yup.array().min(1, "Country is required"),
  subjectId: yup.array().min(1, "Subject is required"),
  keywords: yup.string().required("Keywords is required"),
  disclaimerId: yup.object().nullable().required("Disclaimer is required"),
});
const ForumDetails = ({
  PreviewModal,
  setStatus,
  status,
  setThumbnailId,
  setAutoPopulate,
}) => {
  const {
    register,
    formState,
    setValue,
    getValues,
    clearErrors,
    setError,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const { errors } = formState;

  const isMobile = useMediaQuery("(max-width:600px)");
  const isMedium = useMediaQuery("(max-width:900px)");
  const { sectorFilter, laguageFilter, subjectFilter, disclaimers } =
    useSelector((store) => store.internalFilter);
  const [countries, setCountries] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [disclaimer, setDisclaimer] = useState([]);
  const [thumbnailMessage, setThumbnailError] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const debouncedTitle = useDebounce(title, 500);
  const [description, setDescription] = useState("");
  const [keywords, setKeyWords] = useState();
  const [forumDetails, setForumDetails] = useState(null);
  const { id } = useParams();
  const forumFetch = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    selectedImage,
    isDropped,
    getRootProps,
    getInputProps,
    handleReplace,
    handleDelete,
    setIsDropped,
    setSelectedImage,
  } = useSingleImageDrop();

  useEffect(() => {
    setValue("sectorId", []);
    setValue("subjectId", []);
    setValue("countryId", []);
  }, [setValue]);

  useEffect(() => {
    if (id) {
      if (!forumFetch.current) {
        forumFetch.current = true;
        fetchForumData();
      }
    }
  }, [id]);
  useEffect(() => {
    // Only run if there's something selected
    if (countryList.length > 0) {
      const availableCountryIds = countries.map((c) => c.id);
      const validCountries = countryList.filter((c) =>
        availableCountryIds.includes(c.id)
      );
      if (validCountries.length !== countryList.length) {
        setCountryList(validCountries);
        setValue("countryId", validCountries);
      }
    }
  }, [countries, countryList, setValue]);
  useEffect(() => {
    if (selectedSectors.length === 0) {
      setCountryList([]);
      setValue("countryId", []);
    }
  }, [selectedSectors, setValue]);

  const fetchForumData = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`${BASE_URL}/forum/forum-builder/${id}`);
      if (response?.status === 200) {
        const details = response?.data?.details || {};
        setForumDetails({ details, data: response.data });
        const url = await fetchAndCacheImage(response?.data?.thumbnailFileId);
        const fakeFile = {
          data: url,
          preview: url,
        };
        setAutoPopulate(response?.data);
        setThumbnailId(response?.data?.thumbnailFileId);
        setSelectedImage([fakeFile]);
        setIsDropped(true);
        setTitle(response?.data?.title);
        setValue("title", response?.data?.title);
        setDescription(response?.data?.description || "");
        setValue("description", response?.data?.description || "");

        setStatus(response?.data?.status);

        const finalData = normalizeForumMedia(response?.data);

        // Associated content
        dispatch(
          setAssociatedContent({
            contentType: "document",
            data: finalData?.documents,
          })
        );
        dispatch(
          setAssociatedContent({
            contentType: "video",
            data: finalData?.videos,
          })
        );
        dispatch(
          setAssociatedContent({
            contentType: "link",
            data: finalData?.links,
          })
        );
      }
      if (response?.status === 404) {
        navigate("/404");
      }
    } catch (e) {
      console.error("Failed to fetch forum data:", e);
    } finally {
      setLoading(false);
    }
  };

  const populateForm = useCallback(
    async (forumDetails) => {
      if (
        forumDetails &&
        laguageFilter?.length &&
        sectorFilter?.length &&
        subjectFilter?.length &&
        disclaimers?.length
      ) {
        const { details, data } = forumDetails;

        // Fill form fields
        autoPopulateFormFields({
          details,
          setValue,
          laguageFilter,
          sectorFilter,
          subjectFilter,
          disclaimers,
          setSelectedLanguage,
          setSelectedSectors,
          setCountryList,
          setSelectedSubjects,
          setDisclaimer,
          setLoading,
          setCountries,
          setKeyWords,
        });

        // Handle thumbnail
      }
    },
    [
      laguageFilter,
      sectorFilter,
      subjectFilter,
      disclaimers,
      dispatch,
      setAutoPopulate,
      setStatus,
      setThumbnailId,
      setValue,
      setSelectedImage,
      setIsDropped,
    ]
  );

  // Effect that calls it
  useEffect(() => {
    if (forumDetails) {
      populateForm(forumDetails);
    }
  }, [forumDetails, populateForm]);

  const onFieldChange = useCallback(
    (field, value) => {
      setValue(field, value, { shouldValidate: true });

      formdetailsValidation(
        getValues("title"),
        getValues("languageId"),
        getValues("sectorId"),
        getValues("countryId"),
        getValues("subjectId"),
        getValues("keywords"),
        getValues("disclaimerId")
      );
    },
    [getValues, setValue, setError, clearErrors]
  );

  const scrollToFirstError = (hasThumbnailError) => {
    // Use requestAnimationFrame to ensure DOM is updated, then retry if needed
    const attemptScroll = (retryCount = 0) => {
      // Check for thumbnail error first (custom validation)
      if (hasThumbnailError) {
        const thumbnailElement = document.querySelector(
          '[data-testid="drop-down"]'
        );
        if (
          thumbnailElement &&
          typeof thumbnailElement.scrollIntoView === "function"
        ) {
          thumbnailElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return true;
        }
      }

      // Get current errors from formState (fresh on each attempt)
      const currentErrors = formState.errors;

      // Define the order of fields as they appear in the form
      const fieldOrder = [
        { name: "title", testId: "text-input-title" },
        { name: "languageId", nameAttr: "languageId" },
        { name: "sectorId", nameAttr: "sector_id" },
        { name: "countryId", nameAttr: "countryId" },
        { name: "subjectId", nameAttr: "subjectId" },
        { name: "keywords", testId: "text-input-keyword" },
        { name: "disclaimerId", nameAttr: "disclaimerId" },
      ];

      // Find the first field with an error
      for (const field of fieldOrder) {
        // Check if this field has an error in formState
        const hasFormError = currentErrors[field.name];

        // Only proceed if we know there's an error OR we're on a retry (DOM might be updating)
        if (hasFormError || retryCount > 0) {
          let element = null;

          // For text inputs, use data-testid
          if (field.testId) {
            const inputElement = document.querySelector(
              `[data-testid="${field.testId}"]`
            );
            if (inputElement) {
              // On first attempt, only scroll if we have form error confirmation
              // On retries, scroll even if error class not yet in DOM
              if (hasFormError || retryCount > 0) {
                // Find the parent Box container to scroll to
                const parentBox =
                  inputElement.closest("form > div > div") ||
                  inputElement.closest("form > div") ||
                  inputElement.parentElement;
                element = parentBox || inputElement;
              }
            }
          }

          // For dropdowns, find by error-label class or name attribute
          if (!element && field.nameAttr) {
            // First try to find dropdown with error-label class (most reliable)
            const errorDropdowns = document.querySelectorAll(
              `.form-dropdown.error-label[name="${field.nameAttr}"]`
            );
            if (errorDropdowns.length > 0) {
              // Find the Grid item containing this dropdown
              element =
                errorDropdowns[0].closest(".MuiGrid-item") ||
                errorDropdowns[0].closest("div[class*='MuiGrid']") ||
                errorDropdowns[0];
            } else if (hasFormError || retryCount > 0) {
              // If we know there's an error from formState, find by name even without error class
              const dropdowns = document.querySelectorAll(
                `[name="${field.nameAttr}"]`
              );
              if (dropdowns.length > 0) {
                // Find the Grid item containing this dropdown
                element =
                  dropdowns[0].closest(".MuiGrid-item") ||
                  dropdowns[0].closest("div[class*='MuiGrid']") ||
                  dropdowns[0];
              }
            }
          }

          if (element && typeof element.scrollIntoView === "function") {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            return true;
          }
        }
      }

      return false;
    };

    // Start with requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Add a delay to ensure error states are applied to DOM
        setTimeout(() => {
          const scrolled = attemptScroll(0);
          // If not found, retry with longer delay
          if (!scrolled) {
            setTimeout(() => {
              const scrolled2 = attemptScroll(1);
              // Final retry with even longer delay
              if (!scrolled2) {
                setTimeout(() => attemptScroll(2), 200);
              }
            }, 150);
          }
        }, 200);
      });
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const isValid = await trigger(); // validate all fields
    const hasThumbnail = !!selectedImage?.[0];
    const data = getValues();
    if (!hasThumbnail) {
      setThumbnailError(true);
    } else {
      setThumbnailError(false);
    }

    if (!isValid || !hasThumbnail) {
      // Scroll to first error - function now queries DOM directly for error elements
      scrollToFirstError(!hasThumbnail);
      return;
    }
    const finldata = { ...data, selectedImage };

    PreviewModal(finldata);
  };

  const { isDuplicate, checking } = useDuplicateTitleCheck({
    url: `${BASE_URL}/forum/validate-title`,
    payload: {
      forumId: id ? id : "",
      title: debouncedTitle,
    },
  });

  return (
    <form
      className=" flex flex-col gap-4 w-full"
      onSubmit={onSubmit}
      id="forum-form"
    >
      {countryLoading && <Loading />}
      {loading && <Loading />}

      <Box
        sx={{
          display: "flex",
          flexDirection: isMedium ? "column" : "row",
          gap: "16px",
        }}
      >
        <Box sx={{ maxWidth: "400px" }}>
          <TextInputField
            required={true}
            name="title"
            label="Forum title"
            value={title}
            errors={errors}
            register={register}
            isMobile={isMobile}
            width="400px"
            onChange={(e) => {
              setTitle(e.target.value);
              onFieldChange("title", e.target.value);
            }}
            maxLength={250}
            datatestid="text-input-title"
          />
        </Box>

        {isDuplicate && (
          <Alert severity="warning" sx={{ mt: 1, maxWidth: "350px" }}>
            A Forum with this title already exists
          </Alert>
        )}
      </Box>

      <TextArea
        name="description"
        className="formdetails"
        showToolbar={false}
        label={"Description"}
        style={{ minHeight: "140px" }}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
          onFieldChange("description", e.target.value);
        }}
      />
      <Box sx={{ width: isMobile ? "100%" : "400px" }}>
        {isDropped ? (
          <ImageList
            images={[...selectedImage]}
            onDelete={handleDelete}
            onReplace={(updatedImages) => {
              const file = updatedImages[0]?.file;
              handleReplace(file);
            }}
          />
        ) : (
          <DropZone
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDarkTheme={false}
            label="Thumbnail"
            informativeText="Drag and drop your file here (only .jpg,.png)"
            uploadImageLabel="Upload Image"
            isRequired
            data-testid="drop-down"
            highlightRequired={thumbnailMessage}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Toggle
          label="Status"
          className="flex flex-row gap-1"
          onUpdate={(value) => {
            setStatus(value);
          }}
          checked={status}
          size="medium"
          value={status ? "Active" : "Inactive"}
          variant="horizontal"
        />
      </Box>
      <Typography
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          alignSelf: "stretch",
          color: "var(--Tokens-Text-Primary, #3A3A3A)",
          fontFamily: "var(--Typography-Font-Family-Heading, Inter)",
          fontSize: "var(--Font-Size-H6, 18px)",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "var(--Line-Height-H6, 28px)",
        }}
      >
        Forum details section
      </Typography>
      <>
        <Grid container spacing={2}>
          {/* Language */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ width: "100%", maxWidth: "400px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                name="languageId"
                placeholder="Select"
                label="Language"
                selection="single"
                className={`form-dropdown ${
                  errors.languageId ? "error-label" : ""
                }`}
                error={errors.languageId}
                isClearable={true}
                isRequired
                selectedValue={[selectedLanguage]}
                style={{ fontSize: "14px", width: "100%" }}
                setSelectedValue={(e) =>
                  handleSingleSelectChange({
                    value: e,
                    setState: setSelectedLanguage,
                    fieldName: "languageId",
                    onFieldChange,
                  })
                }
                childList={laguageFilter?.map((item) => ({
                  id: item.id,
                  displayText: item.name,
                }))}
              />
            </Box>
          </Grid>

          {/* Sector */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <Dropdown
                name="sector_id"
                placeholder="Select"
                label="Sectors"
                selection="multiple"
                isClearable
                isRequired
                selectedValue={selectedSectors}
                className={`form-dropdown ${
                  errors.sectorId ? "error-label" : ""
                }`}
                error={errors.sectorId}
                style={{ fontSize: "14px", width: "100%" }}
                childList={sectorFilter?.map((item) => ({
                  id: item.id,
                  displayText: item.name,
                }))}
                setSelectedValue={(newSectors) => {
                  // Call your existing multi-select handler
                  handleMultiSelectChange({
                    value: newSectors,
                    setState: setSelectedSectors,
                    fieldName: "sectorId",
                    onFieldChange,
                    setCountryState: setCountries,
                    setLoading: setCountryLoading,
                  });
                }}
              />
            </Box>
          </Grid>

          {/* Country */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <Dropdown
                name="countryId"
                placeholder="Select"
                label="Country"
                selection="multiple"
                className={`form-dropdown ${
                  errors.countryId ? "error-label" : ""
                }`}
                error={errors.countryId}
                isClearable={true} // <-- Important to allow clearing
                isRequired
                selectedValue={countryList} // <-- Controlled value
                style={{ fontSize: "14px", width: "100%" }}
                setSelectedValue={(e) =>
                  handleMultiSelectChange({
                    value: e,
                    setState: setCountryList,
                    fieldName: "countryId",
                    onFieldChange,
                  })
                }
                childList={countries?.map((item) => ({
                  id: item.id,
                  displayText: item.name,
                }))}
              />
            </Box>
          </Grid>

          {/* Subject */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <Dropdown
                name="subjectId"
                placeholder="Select"
                label="Subjects"
                selection="multiple"
                isClearable
                isRequired
                selectedValue={selectedSubjects} // <-- fixed here
                className={`form-dropdown ${
                  errors.subjectId ? "error-label" : ""
                }`}
                error={errors.subjectId}
                style={{ fontSize: "14px", width: "100%" }}
                childList={subjectFilter?.map((item) => ({
                  id: item.id,
                  displayText: item.name,
                }))}
                setSelectedValue={(e) =>
                  handleMultiSelectChange({
                    value: e,
                    setState: setSelectedSubjects,
                    fieldName: "subjectId",
                    onFieldChange,
                  })
                }
              />
            </Box>
          </Grid>

          {/* Keywords */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <TextInputField
                required
                name="keywords"
                label="Keywords (comma separated)"
                errors={errors}
                register={register}
                isMobile={isMobile}
                value={keywords}
                width="100%"
                onChange={(e) => {
                  setKeyWords(e.target.value);
                  onFieldChange("keywords", e.target.value);
                }}
                datatestid="text-input-keyword"
              />
            </Box>
          </Grid>

          {/* Disclaimer */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <Dropdown
                name="disclaimerId"
                placeholder="Select"
                label="Disclaimer"
                selection="single"
                className={`form-dropdown ${
                  errors.disclaimerId ? "error-label" : ""
                }`}
                error={errors.disclaimerId}
                isClearable={true} // <-- Important to allow clearing
                isRequired
                selectedValue={[disclaimer]} // <-- Controlled value
                style={{ fontSize: "14px", width: "100%" }}
                setSelectedValue={(e) =>
                  handleSingleSelectChange({
                    value: e,
                    setState: setDisclaimer,
                    fieldName: "disclaimerId",
                    onFieldChange,
                  })
                }
                childList={disclaimers?.map((item) => ({
                  id: item.id,
                  displayText: item.name,
                }))}
              />
            </Box>
          </Grid>
        </Grid>
      </>
    </form>
  );
};

export default ForumDetails;
